import { create } from 'zustand';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

export const useIndiaScreenerStore = create((set, get) => ({
  // ── old NSE taxonomy (kept for backward compat) ──
  taxonomy: [],
  taxonomyLoading: false,

  // ── AI taxonomy (sectors → industries + tags) ──
  aiTaxonomy: { sectors: [], tags: [] },
  aiTaxonomyLoading: false,

  // ── securities list ──
  securities: [],
  total: 0,
  loading: false,

  // ── coverage stats ──
  coverage: { total: 0, classified: 0, unclassified: 0, last_refresh: null },
  universeSyncStatus: null,
  refreshingUniverse: false,

  // ── filters ──
  filters: {
    search: '',
    macroSectorIds: [],
    sectorIds: [],
    industryIds: [],
    basicIndustryIds: [],
    exchange: null,
    aiSectors: [],
    aiIndustries: [],
    aiTag: null,
  },

  // ── pagination ──
  page: 1,
  pageSize: 50,
  sortBy: 'company_name',
  sortDir: 'asc',

  // ── actions ──

  fetchTaxonomy: async () => {
    if (get().taxonomy.length > 0) return;
    set({ taxonomyLoading: true });
    try {
      const { data } = await axios.get(`${API}/taxonomy/tree`);
      set({ taxonomy: data });
    } catch (err) {
      console.error('Failed to fetch taxonomy:', err);
    } finally {
      set({ taxonomyLoading: false });
    }
  },

  fetchAiTaxonomy: async () => {
    if (get().aiTaxonomy.sectors.length > 0) return;
    set({ aiTaxonomyLoading: true });
    try {
      const { data } = await axios.get(`${API}/ai-taxonomy`);
      set({ aiTaxonomy: data });
    } catch (err) {
      console.error('Failed to fetch AI taxonomy:', err);
    } finally {
      set({ aiTaxonomyLoading: false });
    }
  },

  fetchCoverage: async () => {
    try {
      const { data } = await axios.get(`${API}/coverage`);
      set({ coverage: data });
    } catch (err) {
      console.error('Failed to fetch coverage:', err);
    }
  },

  triggerUniverseRefresh: async ({ runClassify = false, runEnrich = false } = {}) => {
    set({ refreshingUniverse: true });
    try {
      await axios.post(`${API}/ingestion/universe-refresh`, {
        run_classify: runClassify,
        run_enrich: runEnrich,
      });
      set({ universeSyncStatus: { status: 'running' } });
    } catch (err) {
      console.error('Failed to trigger universe refresh:', err);
      set({ universeSyncStatus: { status: 'error', error: err?.message || 'Failed to trigger refresh' } });
      throw err;
    } finally {
      set({ refreshingUniverse: false });
    }
  },

  fetchUniverseSyncStatus: async () => {
    try {
      const { data } = await axios.get(`${API}/ingestion/universe-refresh/status`);
      set({ universeSyncStatus: data });
      return data;
    } catch (err) {
      console.error('Failed to fetch universe sync status:', err);
      return null;
    }
  },

  fetchSecurities: async () => {
    const { filters, page, pageSize, sortBy, sortDir } = get();
    set({ loading: true });
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.macroSectorIds.length) params.set('macro_sector_ids', filters.macroSectorIds.join(','));
      if (filters.sectorIds.length) params.set('sector_ids', filters.sectorIds.join(','));
      if (filters.industryIds.length) params.set('industry_ids', filters.industryIds.join(','));
      if (filters.basicIndustryIds.length) params.set('basic_industry_ids', filters.basicIndustryIds.join(','));
      if (filters.exchange) params.set('exchange', filters.exchange);
      if (filters.aiSectors.length) params.set('ai_sectors', filters.aiSectors.join(','));
      if (filters.aiIndustries.length) params.set('ai_industries', filters.aiIndustries.join(','));
      if (filters.aiTag) params.set('ai_tag', filters.aiTag);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('sort_by', sortBy);
      params.set('sort_dir', sortDir);

      const { data } = await axios.get(`${API}/securities?${params.toString()}`);
      set({ securities: data.items, total: data.total });
    } catch (err) {
      console.error('Failed to fetch securities:', err);
    } finally {
      set({ loading: false });
    }
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
      page: 1,
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        search: '',
        macroSectorIds: [],
        sectorIds: [],
        industryIds: [],
        basicIndustryIds: [],
        exchange: null,
        aiSectors: [],
        aiIndustries: [],
        aiTag: null,
      },
      page: 1,
    });
  },

  setPage: (page) => set({ page }),

  setSort: (sortBy, sortDir) => set({ sortBy, sortDir, page: 1 }),

  exportCSV: () => {
    const { filters, sortBy, sortDir } = get();
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.macroSectorIds.length) params.set('macro_sector_ids', filters.macroSectorIds.join(','));
    if (filters.sectorIds.length) params.set('sector_ids', filters.sectorIds.join(','));
    if (filters.industryIds.length) params.set('industry_ids', filters.industryIds.join(','));
    if (filters.basicIndustryIds.length) params.set('basic_industry_ids', filters.basicIndustryIds.join(','));
    if (filters.exchange) params.set('exchange', filters.exchange);
    if (filters.aiSectors.length) params.set('ai_sectors', filters.aiSectors.join(','));
    if (filters.aiIndustries.length) params.set('ai_industries', filters.aiIndustries.join(','));
    if (filters.aiTag) params.set('ai_tag', filters.aiTag);
    params.set('sort_by', sortBy);
    params.set('sort_dir', sortDir);
    window.open(`${API}/securities/export?${params.toString()}`, '_blank');
  },
}));
